import React, { useMemo, useCallback, useContext } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import { useLocation, Link } from 'react-router-dom';
import { useParams } from 'react-router';
import isNumber from 'lodash/isNumber';

// GLOBAL VARIABLES
import { INTERFACE, RECORD } from 'pathnames.js';
import { ENDPOINTS } from 'endpoints';

// GLOBAL VARIABLES
import { RETAIL_WHOLESALE } from 'codes';

// GLOBAL FUNCTIONS
import { makePath, apiFetch, modalFunctions, doCallback, formatDate } from 'functions.js';

// HELPERS
import useRecordView from 'helpers/getRecordView';
import { RecordContext } from 'helpers/getRecordData';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import Attachment from 'routers/interface/tools/attachment/Attachment';

// LOCAL VARIABLES
import {
  VEHICLE_MAKE_OPTION_KEYS,
  WARRANTY_OPTION_KEYS,
  EMPLOYEE_OPTION_KEYS,
  LOOKUP_PARAMS,
  YES_NO_NUMERIC
} from '../variables';

const TradeView = ({ data, Open }) => {
  // CONTEXT
  const { refetchRecord } = useContext(RecordContext) || {};
  const {
    values: { sale_type_category_id },
    setFieldValue
  } = useFormikContext() || {};

  const { isAmFinance, isView } = useRecordView();
  const isWholesale = useMemo(
    () => parseInt(sale_type_category_id) === RETAIL_WHOLESALE.wholesale.numeric,
    [sale_type_category_id]
  );

  const { recordID } = useParams();
  const location = useLocation();

  const handleDeleteInTrade = useCallback(
    (trade, salesId, tradeId) => {
      modalFunctions.add({
        type: 'form',
        title: 'Delete Trade?',
        args: {
          method: 'DELETE',
          endpoint: ENDPOINTS.sales.trade.delete(salesId, tradeId),
          onSuccess: () => doCallback(refetchRecord),
          errorMessage: 'Unable to delete Trade.'
        },
        submitButton: {
          label: 'Yes'
        },
        fields: [
          <Form.Col>
            <p><span className="text-danger">Alert:</span> By deleting Stock# {trade?.inventory?.stock_num} ({trade?.inventory?.vehicle_year} {trade?.inventory?.make} {trade?.inventory?.model}) the system will automatically set this vehicle to returned status and it will no longer be available in your inventory.</p>
            <p>Would you like to proceed?</p>
          </Form.Col>,
          <Form.Textarea name="returnedComment" placeholder="Please enter your comment here" required fullWidth />
        ]
      });
    },
    [refetchRecord]
  );

  const handleDeleteSoldTrade = useCallback(item => {
    modalFunctions.add({
      type: 'dialog',
      title: 'Delete Trade?',
      body: (
        <div className="px-2">
          <span className="text-danger">Alert:</span> The trade with Stock#: {item.stock_num} ({item.vehicle_year}{' '}
          {item.make} {item.model}) has been sold and cannot be deleted at this time. You will need to backout the deal
          for (Stock# {item.stock_num}) first in order to delete the trade from this deal.
        </div>
      ),
      buttons: [<Button.Cancel label="Cancel" />]
    });
  }, []);

  const deleteNewTrade = useCallback(
    index => {
      const tempArray = [...data];
      if (index !== -1) {
        tempArray.splice(index, 1);
      }
      setFieldValue('aryTrade', tempArray);
    },
    [data, setFieldValue]
  );

  const handleDeleteNewTrade = useCallback(
    index => {
      modalFunctions.add({
        type: 'form',
        title: 'Delete Trade?',
        continueButton: {
          label: 'Yes',
          onClick: () => deleteNewTrade(index)
        },
        fields: [
          <Form.Col>
            <p><span className="text-danger">Alert:</span> Are you sure you want to delete this trade?</p>
          </Form.Col>,
          <Form.Textarea name="returnedComment" placeholder="Please enter your comment here" fullWidth />
        ]
      });
    },
    [deleteNewTrade]
  );

  const handleDelete = (salesId, index, trade, tradeId, inventoryStatusCode) => {
    !tradeId
      ? handleDeleteNewTrade(index)
      : ['I', 'B', 'F', 'PS'].includes(inventoryStatusCode)
      ? handleDeleteInTrade(trade, salesId, tradeId)
      : apiFetch({
          endpoint: ENDPOINTS.sales.trade.get(salesId, tradeId),
          loadingMessage: 'Fetching Trade Information!',
          onSuccess: data => handleDeleteSoldTrade(data)
        });
  };

  const sifLabel = useMemo(() => {
    return isView ? 'SIF in Possession' : 'Do you have SIF?';
  }, [isView]);

  return (
    <>
      {Array.isArray(data)
        ? data?.map((item = {}, i) => {
            const {
              stock_num,
              vin_num,
              vehicle_year,
              make,
              make_id,
              model,
              model_code,
              vehicle_trim,
              body_style,
              mileage,
              ext_color,
              int_color,
              engine,
              engine_type,
              transmission_name,
              drivetrain_name,
              retail_price,
              internet_price,
              invoice_cost,
              purchase_date,
              source_name,
              appraised_by_user_id,
              appraised_by,
              warranty_type,
              warranty_type_name,
              daily_sales_inventory_id,
              inventory_status_code,
              documentAttachment
            } = isView ? item : item.inventory || {};
            return (
              isNumber(daily_sales_inventory_id) && (
                <Form.Body className="mx-2 my-3" key={`${data}.[${i}]+${i}`}>
                  {isAmFinance && i !== 0 ? <hr /> : null}
                  <Form.Col cols={{ xs: 24 }}>
                    <h4 className="h5">{`Trade ${i + 1}`}</h4>
                  </Form.Col>
                  <Form.PlainText
                    label="Stock #"
                    value={
                      item?.trade_id ? (
                        <Link
                          className="text-blue"
                          to={makePath(INTERFACE.inventory, RECORD.view, daily_sales_inventory_id)}
                          state={{ prevPath: location.pathname }}
                        >
                          {stock_num}
                        </Link>
                      ) : (
                        stock_num
                      )
                    }
                    copyToClipboard
                  />
                  <Form.PlainText label="VIN" value={vin_num} copyToClipboard />
                  <Attachment.Link label="Photo Proof of VIN" attachments={documentAttachment?.vin_photo} />
                  {!isAmFinance && !isWholesale ? <hr /> : null}
                  <Form.PlainText label="Year" value={vehicle_year} />
                  {isView ? (
                    <Form.PlainText label="Make" value={make} />
                  ) : (
                    <Form.Select
                      value={make_id}
                      label="Make"
                      lookup="vehicleMakes"
                      optionKeys={VEHICLE_MAKE_OPTION_KEYS}
                      plaintext
                    />
                  )}
                  <Form.PlainText label="Model" value={model} />
                  <Form.PlainText label="Model Code" value={model_code} />
                  <Form.PlainText label="Trim" value={vehicle_trim} copyToClipboard />
                  <Form.PlainText label="Body Style" value={body_style} />
                  <Form.PlainText label="Mileage" value={mileage} schema="miles" copyToClipboard />
                  <Attachment.Link label="Photo Proof of Mileage" attachments={documentAttachment?.mileage_photo} />
                  <Form.PlainText label="Exterior" value={ext_color} copyToClipboard />
                  <Form.PlainText label="Interior" value={int_color} copyToClipboard />
                  <Form.PlainText label="Engine" value={engine} />
                  <Form.PlainText label="Engine Type" value={engine_type} />
                  <Form.PlainText label="Trasmission" value={transmission_name} />
                  <Form.PlainText label="Drivetrain" value={drivetrain_name} />
                  {isView ? (
                    <Form.PlainText label="Warranty Type" value={warranty_type_name} />
                  ) : (
                    <Form.Select
                      value={warranty_type}
                      label="Warranty type"
                      lookup="warrantyTypes"
                      optionKeys={WARRANTY_OPTION_KEYS}
                      plaintext
                    />
                  )}
                  {!isAmFinance && !isWholesale ? <hr /> : null}
                  {!isWholesale ? (
                    <>
                      <Form.PlainText label="ACV" value={item?.acv || 0} schema="dollars" />
                      {!isView ? (
                        <Form.Select
                          value={appraised_by_user_id}
                          label="Appraised by"
                          lookup="salesManagers"
                          lookupParams={LOOKUP_PARAMS.salesManager}
                          optionKeys={EMPLOYEE_OPTION_KEYS}
                          plaintext
                        />
                      ) : null}
                      <Form.PlainText
                        label="Trade In Allowance"
                        value={item?.trade_in_allowance || 0}
                        schema="dollars"
                      />
                      {!isWholesale &&
                        <>
                          <Attachment.Link label="Trade Appraisal" attachments={documentAttachment?.trade_appraisal} />
                          <Form.YesNo
                            label="Trade Inspection Completed by Service?"
                            value={item?.trade_inspection_completed}
                            plaintext
                          />
                          <Attachment.Link label="Trade Inspection" attachments={documentAttachment?.trade_inspection} />
                        </>
                      }
                      <Form.PlainText
                        label="Payoff"
                        value={
                          parseInt(item?.need_to_pay_off) === 0
                            ? 'No'
                            : parseInt(item?.need_to_pay_off) === 1
                            ? 'Yes'
                            : ''
                        }
                      />
                      {parseInt(item?.need_to_pay_off) === 1 && (
                        <>
                          <Form.PlainText label="Bank To Payoff" value={item.bank_to_pay_off} />
                          <Form.PlainText label="Loan Account #" value={item.loan_account_number} />
                          <Form.PlainText label="Phone" value={item.phone_number} />
                          <Form.PlainText label="Contact" value={item.contact} />
                          <Form.PlainText label="Payoff Amount" value={item.pay_off_amount} schema="dollars" />
                          <Form.PlainText label="Payoff Good Until" value={formatDate(item.good_until)} />
                          <Form.PlainText label="Per Diem" value={item.per_diem} schema="dollars" />
                        </>
                      )}
                      <Form.PlainText label="Trade Title State" value={item?.title_state_code} />
                      <Form.PlainText
                        label="Title in Possession"
                        value={YES_NO_NUMERIC[parseInt(item.title_in_possession)] || 'N/A'}
                      />
                      {!isWholesale &&
                        <Attachment.Link label="Title in Possession" attachments={documentAttachment?.trade_title} />
                      }
                      {parseInt(item?.title_in_possession) === 0 && (
                        <Form.PlainText label="Title Available On" value={formatDate(item.trade_available_on)} />
                      )}
                      <Form.PlainText label={sifLabel} value={YES_NO_NUMERIC[parseInt(item.has_sif)] || 'N/A'} />
                      <Form.PlainText
                        label="Vehicle Registration"
                        value={YES_NO_NUMERIC[parseInt(item.vehicle_registration)] || 'No'}
                      />
                      {!isWholesale &&
                        <Attachment.Link label="Vehicle Registration" attachments={documentAttachment?.trade_registration} />
                      }
                    </>
                  ) : (
                    <>
                      <Form.PlainText label="MSRP" schema="dollars" value={retail_price || 0} />
                      <Attachment.Link label="MSRP Sticker/Window Sticker" attachments={documentAttachment?.msrp} />
                      <Form.PlainText label="Internet" schema="dollars" value={internet_price || 0} />
                      <Form.PlainText label="Invoice Cost" schema="dollars" value={invoice_cost || 0} />
                      <Attachment.Link label="Factory Invoice" attachments={documentAttachment?.invoice} />
                      <Form.PlainText label="Purchase Date" value={purchase_date ? formatDate(purchase_date) : ''} />
                      <Form.PlainText value={'Dealer DX'} label="Source" />
                      <Form.PlainText label="Source Name (DX from Dealer Name)" value={source_name} />
                      {isView ? (
                        <Form.PlainText value={appraised_by} label="DX Transacted / Acquired By" />
                      ) : (
                        <Form.Select
                          value={appraised_by_user_id}
                          label="DX Transacted / Acquired By"
                          lookup="salesManagers"
                          lookupParams={LOOKUP_PARAMS.salesManager}
                          optionKeys={EMPLOYEE_OPTION_KEYS}
                          plaintext
                        />
                      )}
                      <Attachment.Link label="Other" attachments={documentAttachment?.other} />
                      <Attachment.Link label="DX Documents" attachments={documentAttachment?.dx} />
                    </>
                  )}
                  {!isView && <Form.PlainText label=" " value={<>
                    <Button.Link icon="edit" label="Edit" onClick={() => Open(data, item)} />
                    <Button.Link
                      className="ms-4"
                      icon="delete"
                      label="Delete"
                      onClick={() => handleDelete(recordID, i, item, item.trade_id, inventory_status_code)}
                    />
                  </>} />}
                </Form.Body>
              )
            );
          })
        : null}
    </>
  );
};

export default TradeView;
